import React, {
  ReactNode,
  FunctionComponent,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import {
  Loader,
  Message,
  Icon,
} from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import classNames from "classnames";

import "canvas-toBlob";
import "cropperjs/dist/cropper.css";

import constants from "../../../constants";
import { IMAGE_TOO_BIG_ERROR_MESSAGE } from "../../../constants/customErrorMessages";
import {
  Entity,
  ImageFormat,
  UploadType,
} from "../../../constants/imageUpload";
import { convertImageAndDrop } from "../../../helpers/imageCropperHelper";
import UploadImageService from "../../../modules/uploadEditorImage";

import CropperModal from "./cropperModal";

import "./index.css";

const bytesInMegabyte = 1048576;

interface ImageState {
  preview: string;
  fileName: string;
  imageType: string;
  width: number;
  height: number;
}

export interface ImageUploadProps {
  entity: Entity;
  uploadType: UploadType;
  width: number;
  height: number;
  formats?: ImageFormat[];
  disabled?: boolean;
  clearable?: boolean;
  disableCropping?: boolean;
  disableLoader?: boolean;
  showImageUrl?: boolean;
  position?: "top" | "left";
  children?: ReactNode;
  onChange?: (url?: string) => void;
}

export const ImageUpload: FunctionComponent<ImageUploadProps> = ({
  position = "top",
  disabled,
  clearable,
  entity,
  uploadType,
  formats,
  children,
  showImageUrl,
  disableCropping,
  disableLoader = false,
  onChange
}) => {
  const sessionKey = useSelector((state: any) => state.session.key);

  const [imageState, setImageState] = useState<ImageState | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);

  const classes = useMemo(() => ({
    dropzoneClass: classNames("dropzone", { [`dropzone-${position}`]: true }),
    loaderClass: classNames("image-loader")
  }), [position]);

  const removeButtonStyles = useMemo(() => {
    const styles = {
      left: {
        left: "140px"
      },
      top: {
        right: "17px"
      }
    };

    return styles[position];
  }, [position]);

  const onDrop = useCallback((files: File[]) => {
    if (files && files.length === 1) {
      const file = Object.assign(files[0], {
        preview: URL.createObjectURL(files[0]),
      });

      if (disableCropping) {
        convertImageAndDrop(file, handleImageDrop, disableCropping);

        uploadImage(file);
      } else if (file.size < constants.maxImageSize * bytesInMegabyte) {
        convertImageAndDrop(file, handleImageDrop, disableCropping);
      } else {
        setError(IMAGE_TOO_BIG_ERROR_MESSAGE);
      }
    }
  }, [disableCropping]);

  const {
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: {
      "image/*": [
        ...(formats || []).map(f => `.${f.toLowerCase()}`),
      ],
    },
    disabled: disabled || isUploading,
    onDrop,
  });

  const handleImageDrop = useCallback((
    preview: string,
    fileName: string,
    width: number,
    height: number,
    imageType: string
  ) => {
    setImageState({
      preview,
      fileName,
      width,
      height,
      imageType
    });
    setError(null);

    if (disableCropping) {
      // For direct upload without cropping, we need the original file
      // This would need to be handled differently - you might want to store the original file
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
  }, [disableCropping]);

  const uploadImage = useCallback(async (fileOrBlob: File | Blob) => {
    if (!sessionKey) {
      setError("Authentication required for upload");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const response = await UploadImageService.uploadBlob(
        entity,
        uploadType,
        fileOrBlob,
        sessionKey,
        (progress: number) => setUploadProgress(progress)
      );

      const imageUrl = response.data.data;
      setUploadedImageUrl(imageUrl);

      if (onChange) {
        onChange(imageUrl);
      }

      // Clear the modal state
      setImageState(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Upload failed:", error);
      setError("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  }, [entity, uploadType, sessionKey, onChange]);

  const handleCropperConfirm = useCallback((croppedBlob: Blob, imageUrl?: string) => {
    uploadImage(croppedBlob);
  }, [uploadImage]);

  const handleCropperCancel = useCallback(() => {
    setImageState(null);
    setIsModalOpen(false);
    setError(null);
  }, []);

  const handleClearImage = useCallback(() => {
    setUploadedImageUrl(null);
    setImageState(null);
    setError(null);

    if (onChange) {
      onChange(undefined);
    }
  }, [onChange]);

  const displayRemoveButton = (clearable || uploadedImageUrl) && !disabled;

  return (
    <>
      {displayRemoveButton && (
        <div className="remove-image-button-container">
          <Icon
            className="remove-image-button"
            name="remove circle"
            size="big"
            style={removeButtonStyles}
            onClick={handleClearImage}
          />
        </div>
      )}

      <div {...getRootProps({ className: classes.dropzoneClass })}>
        <input {...getInputProps()} />
        {children}
        {isUploading && !disableLoader && (
          <div className={classes.loaderClass}>
            <Loader active inline="centered" />
            <div>Uploading... {uploadProgress}%</div>
          </div>
        )}
      </div>

      {error && (
        <Message
          negative
          content={error}
        />
      )}

      {imageState && !disableCropping && (
        <CropperModal
          isOpen={isModalOpen}
          imagePreview={imageState.preview}
          fileName={imageState.fileName}
          imageType={imageState.imageType}
          width={imageState.width}
          height={imageState.height}
          uploadType={uploadType}
          showImageUrl={showImageUrl}
          isUploading={isUploading}
          onConfirm={handleCropperConfirm}
          onCancel={handleCropperCancel}
        />
      )}
    </>
  );
};
